import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createConversationQuickReply,
    fetchConversationQuickReplies,
    updateConversationQuickReply,
    type ConversationQuickReply,
    type CreateQuickReplyBody,
    type QuickReplyListFilter,
    type UpdateQuickReplyBody
} from '../services/conversationQuickReplies';
import { CONVERSATION_QUICK_REPLIES_KEY, CONVERSATION_QUICK_REPLIES_LIST_KEY } from './queryKeys';

const QUICK_REPLIES_STALE_TIME_MS = 5 * 60 * 1000;

// Stable identity: the fallback feeds memoised filtering.
const EMPTY_REPLIES: ConversationQuickReply[] = [];

export function useConversationQuickReplies(filter: QuickReplyListFilter) {
    const { data, error, isLoading, isFetching, isError, refetch } = useQuery({
        queryKey: CONVERSATION_QUICK_REPLIES_LIST_KEY(filter),
        queryFn: () => fetchConversationQuickReplies(filter),
        staleTime: QUICK_REPLIES_STALE_TIME_MS
    });

    return { data: data ?? EMPTY_REPLIES, error, isLoading, isFetching, isError, refetch };
}

export function useCreateConversationQuickReply() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateQuickReplyBody) => createConversationQuickReply(body),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: CONVERSATION_QUICK_REPLIES_KEY });
        }
    });
}

export function useUpdateConversationQuickReply() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, body }: { id: string; body: UpdateQuickReplyBody }) =>
            updateConversationQuickReply(id, body),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: CONVERSATION_QUICK_REPLIES_KEY });
        }
    });
}
